import { Col, Row } from '@amzn/stencil-react-components/layout';
import { H3 } from '@amzn/stencil-react-components/text';
import { Button, ButtonVariant, ButtonSize } from '@amzn/stencil-react-components/button';
import { Badge, BadgeType } from '@amzn/stencil-react-components/badge';
import { IconBinSmall } from '@amzn/stencil-react-icons';

import { AlertTriangleFillIcon } from '../../../shared/ui/AlertTriangleFillIcon';

/** Header title toggles the expander on click. Module-level so the emotion css
 *  object is reference-stable. */
const CLICKABLE_TITLE = { cursor: 'pointer' } as const;

/** Keeps a trailing badge/marker at its intrinsic width so a long title can't
 *  squeeze (and clip) it. Module-level for emotion reference stability. */
const NO_SHRINK = { flexShrink: 0 } as const;

export interface SubQuestionRowHeaderProps {
  /** The Expander's composed title text (e.g. "1. My sub-question"). Stencil's
   *  Expander types this as possibly-undefined, so accept undefined explicitly
   *  (exactOptionalPropertyTypes). */
  readonly titleText?: string | undefined;
  /** Toggles the surrounding Expander. */
  readonly onToggle: () => void;
  /** Response-type label shown in the badge (e.g. "Pass / Fail"). The badge is
   *  purely informational (always the same color) — it says WHAT type the sub
   *  is, never whether it has a problem. Row-level problems are flagged by the
   *  separate warning marker ({@link hasIssue}). */
  readonly typeBadgeLabel: string;
  /** When true, a warning marker flags this row as having a validation issue —
   *  the single, consistent signal for ANY row-level problem (a select type
   *  with fewer than two options, a Pass/Fail sub blocking conditional display,
   *  and future required-field errors). The specific message lives in the
   *  banner; this marker just says "fix this row". */
  readonly hasIssue: boolean;
  /** Accessible name for the warning marker (e.g. "Needs attention"). */
  readonly issueLabel: string;
  readonly onDelete: (e: React.MouseEvent) => void;
  readonly deleteAltText: string;
  readonly disabled: boolean;
  readonly dataTestId?: string;
}

/**
 * The collapsed row header for a sub-question: the composed title (click to
 * expand), an informational response-type {@link Badge} so the type is
 * scannable without expanding, an optional warning marker when the row has a
 * validation issue, and a right-anchored delete button. Extracted from
 * {@link SubQuestionRow} to keep that file under the size limit.
 */
export function SubQuestionRowHeader({
  titleText,
  onToggle,
  typeBadgeLabel,
  hasIssue,
  issueLabel,
  onDelete,
  deleteAltText,
  disabled,
  dataTestId,
}: SubQuestionRowHeaderProps): JSX.Element {
  return (
    <Row alignItems="center" gridGap="dimensions.spacing.200" flex={1} minWidth={0}>
      {/* The title is the ONLY flexible element: it grows to fill the row and
          shrinks/truncates when space is tight (flex={1} minWidth={0}), pushing
          the badges + delete to the right. The trailing items below each carry
          `flexShrink: 0` so the browser can't squeeze them below their content
          width — without that, a long title compressed the type badge and
          clipped its label. */}
      <Col flex={1} minWidth={0}>
        <H3 onClick={onToggle} css={CLICKABLE_TITLE}>
          {titleText}
        </H3>
      </Col>
      {/* Wrap each Badge in a Row carrying the test-id: Stencil's Badge prop
          filter drops `dataTestId`, so it never reaches the DOM (same issue +
          fix documented on PerformAuditQuestionCard's result badge). The Row
          forwards it as `data-test-id`, so external/DOM tests can locate it. */}
      <Row
        css={NO_SHRINK}
        {...(dataTestId ? { dataTestId: `${dataTestId}-type-badge` } : {})}
      >
        {/* Always informational (Primary): the badge names the type, never the
            error state. Problems are flagged by the warning marker below. */}
        <Badge type={BadgeType.Primary} value={typeBadgeLabel} />
      </Row>
      {hasIssue ? (
        // The single row-level problem marker — one warning icon for ANY issue
        // (min-options, conditional-display block, future required-field
        // errors). Stencil icons are decorative (aria-hidden, and they drop
        // aria-label), so the accessible name lives on the wrapping Row via
        // role="img" + aria-label; the icon is purely visual. The Row also
        // carries the test-id (Stencil icons, like Badge, don't forward
        // dataTestId).
        <Row
          role="img"
          aria-label={issueLabel}
          css={NO_SHRINK}
          {...(dataTestId ? { dataTestId: `${dataTestId}-issue-marker` } : {})}
        >
          <AlertTriangleFillIcon />
        </Row>
      ) : null}
      <Button
        variant={ButtonVariant.Tertiary}
        size={ButtonSize.Small}
        icon={<IconBinSmall aria-hidden="true" />}
        aria-label={deleteAltText}
        onClick={onDelete}
        disabled={disabled}
        data-testid={dataTestId ? `${dataTestId}-delete` : undefined}
      />
    </Row>
  );
}
